import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { getDaysDiff } from '../../../../../shared/util/date.util';
import { Subscription } from 'rxjs';
import { InvoiceService } from '../../../services/invoice.service';
import { FeatureFlagService } from "src/app/shared/services/feature-flag.service";
import Big from 'big.js';

@Component({
  selector: 'app-collect-invoice-dialog',
  templateUrl: './collect-invoice-dialog.component.html',
  styleUrls: ['./collect-invoice-dialog.component.scss'],
})
export class CollectInvoiceDialogComponent implements OnInit, OnDestroy {
  form: FormGroup;
  dateWarning;
  isChecked = false;
  feeData: any = {};
  private formSubscription: Subscription;
  featureFlagSubscription;
  newRefundAmount;
  delayedDays = 0;
  isInvoicingStopped = false;
  showFeeAjuste = false;
  pendingPrefill: any = null;

  invoicingOptions = [];

  constructor(
    public dialogRef: MatDialogRef<CollectInvoiceDialogComponent>,
    private formBuilder: FormBuilder,
    private invoiceService: InvoiceService,
    private featureFlagService: FeatureFlagService,
    @Inject(MAT_DIALOG_DATA) public data: any
  ) {}

  ngOnInit(): void {
    this.form = this.formBuilder.group({
      actualPaymentDate: ['', [Validators.required]],
      invoicingConfiguration: [''],
    });

    this.data.invoice.currencySymbol =
      this.data.invoice.currency === 'pen' ? 'S/ ' : '$ ';

    this.formSubscription = this.form
      .get('actualPaymentDate')
      .valueChanges.subscribe((selectedDate) => {
        if (!selectedDate) {
          return;
        }

        const daysDiff = getDaysDiff(
          this.data.paymentDate,
          this.data?.partialPaymentDate || selectedDate
        );
        this.isChecked = false;
        this.isInvoicingStopped = false;
        this.feeData = {};
        this.newRefundAmount = undefined;

        this.invoicingOptions = [
          {
            text: 'Factura al girador: Girador asume todo el pago (Se consume la garantía)',
            value: 'normal',
          },
          {
            text: 'Facturación partida: Girador asume solo lo pactado',
            value: 'debtorAll',
          },
        ];

        this.form.controls['invoicingConfiguration'].setValue('normal');

        if (daysDiff < 0) {
          this.dateWarning = 'before';
          this.delayedDays = 0;
        } else if (daysDiff === 0) {
          this.dateWarning = 'notDelayed';
          this.delayedDays = 0;
        } else {
          this.isChecked = true;
          this.delayedDays = daysDiff;
          this.dateWarning = 'delayed';
        }

        this.loadPaymentInformation();

        if (this.pendingPrefill && daysDiff <= 0) {
          this.isInvoicingStopped = !!this.pendingPrefill.isInvoicingStopped;
          this.pendingPrefill = null;
        }
      });

      this.featureFlagSubscription = this.featureFlagService.flags$.subscribe(flags => {
        this.showFeeAjuste = flags.teaFeatureEnabled;
        console.log('showFeeAjuste updated:', this.showFeeAjuste);
      });

    if (this.data?.savedInput && !this.data?.partialPaymentDate) {
      this.pendingPrefill = this.data.savedInput;
      this.form
        .get('actualPaymentDate')
        .setValue(this.data.savedInput.actualPaymentDate);
    } else if (this.data?.partialPaymentDate) {
      this.loadPaymentInformation();
    }
  }

  ngOnDestroy(): void {
    this.formSubscription.unsubscribe();
  }

  onSubmit() {
    this.form.markAllAsTouched();
    this.form.updateValueAndValidity();

    if (this.form.valid) {
      let invoicingConfiguration = '';
      if (this.isInvoicingStopped) {
        invoicingConfiguration = 'noInvoicing';
      } else {
        if (this.data.invoice.invoicingStatus === 'delay') {
          invoicingConfiguration = 'delay';
        } else if (this.data.invoice.isConfirming) {
          invoicingConfiguration = 'normal';
        } else {
          invoicingConfiguration = this.form.value.invoicingConfiguration;
        }
      }

      const extraData = {
        isInvoicingStopped: this.isInvoicingStopped,
        feeData: this.feeData,
        newRefundAmount: this.newRefundAmount,
        showFeeAjuste: this.showFeeAjuste,
      };

      if (this.data?.partialPaymentDate) {
        this.dialogRef.close({
          invoicingConfiguration,
          actualPaymentDate: this.data.partialPaymentDate,
          isFeeApplied: this.isChecked,
          ...extraData,
        });
      } else {
        this.dialogRef.close({
          invoicingConfiguration,
          actualPaymentDate: this.form.value.actualPaymentDate,
          isFeeApplied: this.isChecked,
          ...extraData,
        });
      }
    }
  }

  selectDelayedFee(isChecked) {
    this.isChecked = isChecked;
    if (isChecked) {
      this.loadPaymentInformation();
    }
  }

  stopInvoicing(isChecked) {
    this.isInvoicingStopped = isChecked;
  }

  loadPaymentInformation() {
    const actualPaymentDate =
      this.data.partialPaymentDate || this.form.get('actualPaymentDate').value;

    if (!actualPaymentDate) {
      return;
    }

    this.invoiceService
      .getLatefee(this.data.invoice._id, actualPaymentDate)
      .subscribe((data) => {
        this.feeData = data;
        this.newRefundAmount = new Big(this.data.invoice.reservationAmount)
          .minus(new Big(this.feeData?.newTotalReturn || 0))
          .toFixed(2);

        const daysDiff = getDaysDiff(
          this.data.paymentDate,
          this.data?.partialPaymentDate || actualPaymentDate
        );

        if (daysDiff > 0 && Number(this.newRefundAmount) < 0) {
          if (
            !this.invoicingOptions.some(
              (option) => option.value === 'debtorExtra'
            )
          ) {
            this.invoicingOptions = [
              {
                text: 'Facturación partida: Girador asume hasta agotar garantía',
                value: 'debtorExtra',
              },
              {
                text: 'Facturación partida: Girador asume solo lo pactado',
                value: 'debtorAll',
              },
              {
                text: 'Factura al girador: Girador asume todo el pago (Se consume la garantía)',
                value: 'normal',
              },
            ];
          }

          this.form.controls['invoicingConfiguration'].setValue('debtorExtra');
        } else {
          this.form.controls['invoicingConfiguration'].setValue('normal');
        }

        this.isInvoicingStopped = false;

        if (this.pendingPrefill) {
          this.isChecked = !!this.pendingPrefill.isFeeApplied;
          this.isInvoicingStopped = !!this.pendingPrefill.isInvoicingStopped;
          if (this.pendingPrefill.invoicingConfiguration) {
            this.form.controls['invoicingConfiguration'].setValue(
              this.pendingPrefill.invoicingConfiguration
            );
          }
          this.pendingPrefill = null;
        }
      });
  }
  cancel() {
    this.dialogRef.close();
  }
}
